import { useEffect, useState, useCallback } from "react";
import { api, assetUrl } from "@/lib/api";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { CheckCircle, XCircle, SkipForward, MapPin, Leaf, Sparkle, Translate, GlobeHemisphereEast, Lightning, ArrowClockwise, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const LANG_LABEL = { en: "English", hi: "हिन्दी", ta: "தமிழ்", ml: "മലയാളം", es: "Español", fr: "Français", de: "Deutsch" };

export default function Review() {
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [a, setA] = useState(null);          // analysis for current photo
  const [loadingA, setLoadingA] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState("en");
  const [titleBy, setTitleBy] = useState({});
  const [descBy, setDescBy] = useState({});
  const [altBy, setAltBy] = useState({});
  const [keywords, setKeywords] = useState("");
  const [verifyLoc, setVerifyLoc] = useState(false);
  const [rights, setRights] = useState({});
  const [creator, setCreator] = useState("");
  const [creditLine, setCreditLine] = useState("");

  const load = () => api.get("/review").then((r) => { setQueue(r.data); setIdx(0); }).catch(() => setQueue([]));
  useEffect(() => { load(); }, []);

  const hydrate = useCallback((an) => {
    const cur = an.current || {};
    const aa = an.ai_analysis || {};
    const sug = aa.suggestions || {};
    const i18n = aa.i18n || {};
    const langs = ["en", ...(an.languages || [])];
    const tBy = {}, dBy = {}, alBy = {};
    langs.forEach((lg) => {
      if (lg === "en") {
        tBy.en = cur.title || sug.title || "";
        dBy.en = cur.description || sug.description || "";
        alBy.en = cur.alt_text || sug.alt_text || "";
      } else {
        tBy[lg] = (cur.title_i18n || {})[lg] || (i18n[lg] || {}).title || "";
        dBy[lg] = (cur.description_i18n || {})[lg] || (i18n[lg] || {}).description || "";
        alBy[lg] = (cur.alt_text_i18n || {})[lg] || (i18n[lg] || {}).alt_text || "";
      }
    });
    setTitleBy(tBy); setDescBy(dBy); setAltBy(alBy);
    const kw = (cur.keywords?.length ? cur.keywords : (sug.keywords || [])).join(", ");
    setKeywords(kw);
    setRights(an.rights || {});
    setCreator(an.creator || "");
    setCreditLine(an.credit_line || "");
    setVerifyLoc(false); setLang("en");
  }, []);

  useEffect(() => {
    if (!queue || queue.length === 0) { setA(null); return; }
    const pid = queue[idx]?.id;
    if (!pid) return;
    setLoadingA(true); setA(null);
    api.get(`/photos/${pid}/analysis`).then((r) => { setA(r.data); hydrate(r.data); }).finally(() => setLoadingA(false));
  }, [queue, idx, hydrate]);

  const removeCurrent = () => {
    const next = queue.filter((_, i) => i !== idx);
    setQueue(next);
    if (idx >= next.length) setIdx(Math.max(0, next.length - 1));
  };

  const reject = async () => {
    setBusy(true);
    try { await api.post(`/photos/${queue[idx].id}/review`, { decision: "reject" }); toast.success("Sent back to drafts"); removeCurrent(); }
    catch { toast.error("Could not reject"); } finally { setBusy(false); }
  };

  const approve = async (publish) => {
    if (publish && !(titleBy.en || "").trim()) { toast.error("Add a title before publishing"); return; }
    setBusy(true);
    const langs = ["en", ...(a.languages || [])];
    const pick = (obj) => { const o = {}; langs.filter((l) => l !== "en").forEach((l) => { if ((obj[l] || "").trim()) o[l] = obj[l].trim(); }); return o; };
    const locSug = (a.ai_analysis || {}).location_suggestion;
    const payload = {
      title: (titleBy.en || "").trim() || null,
      description: (descBy.en || "").trim() || null,
      alt_text: (altBy.en || "").trim() || null,
      keywords: keywords.split(",").map((s) => s.trim()).filter(Boolean),
      title_i18n: pick(titleBy), description_i18n: pick(descBy), alt_text_i18n: pick(altBy),
      creator: (creator || "").trim() || null,
      credit_line: (creditLine || "").trim() || null,
      rights,
      publish, make_public: publish,
    };
    if (verifyLoc && locSug) {
      payload.verify_location = true;
      payload.location = { city: locSug.city, country: locSug.country, place_name: locSug.place_name };
    }
    try {
      await api.post(`/photos/${queue[idx].id}/approve`, payload);
      toast.success(publish ? "Published ✓" : "Approved (kept private)");
      removeCurrent();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); } finally { setBusy(false); }
  };

  if (queue === null) return <p className="text-muted-foreground">Loading…</p>;

  if (queue.length === 0)
    return (
      <div className="mx-auto max-w-[1400px]">
        <p className="overline text-[#F27A54]">Human Review</p>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Review Queue</h1>
        <div className="mt-16 grid place-items-center text-center" data-testid="review-empty">
          <CheckCircle size={48} weight="thin" className="text-emerald-400" />
          <p className="mt-3 font-display text-lg font-bold">Queue is clear</p>
          <p className="text-sm text-muted-foreground">Every uploaded photo has been reviewed and approved.</p>
        </div>
      </div>
    );

  const p = queue[idx];
  const aa = a?.ai_analysis || {};
  const sug = aa.suggestions || {};
  const loc = a?.location || {};
  const basis = loc.basis || aa.location_basis;
  const locSug = aa.location_suggestion;
  const langs = ["en", ...((a?.languages) || [])];
  const locConf = basis === "gps" ? 100 : (locSug?.confidence ?? null);

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline text-[#F27A54]">Review &amp; Republish</p>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Review Queue</h1>
          <p className="mt-1 text-sm text-muted-foreground">AI only suggests — nothing goes public until you approve.</p>
        </div>
        <Badge variant="outline" className="rounded-sm text-sm" data-testid="review-counter">{idx + 1} / {queue.length}</Badge>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        {/* Image + location intelligence */}
        <div className="space-y-4">
          <div className="overflow-hidden rounded-xl border border-border bg-zinc-900">
            {a?.media_type === "video"
              ? <video src={assetUrl(a?.video_url)} controls className="max-h-[52vh] w-full object-contain" />
              : <img src={a ? assetUrl(a.image_url) : undefined} alt="" className="max-h-[52vh] w-full object-contain" data-testid="review-image" />}
          </div>

          <div className="rounded-xl border border-border bg-white p-4" data-testid="review-location-card">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-800"><GlobeHemisphereEast size={16} weight="fill" className="text-[#2D6A4F]" /> Location intelligence</p>
              {basis !== "unknown" && locConf != null && <ConfidenceBadge score={locConf} />}
            </div>
            {basis === "gps" ? (
              <p className="mt-2 flex flex-wrap items-center gap-1.5 text-sm text-zinc-700" data-testid="review-loc-gps">
                <MapPin size={15} weight="fill" className="text-[#F27A54]" />
                {[loc.city, loc.state, loc.country].filter(Boolean).join(", ") || "Coordinates on file"}
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-[#2D6A4F]">from GPS · confirmed</span>
              </p>
            ) : locSug ? (
              <div className="mt-2" data-testid="review-loc-ai">
                <p className="flex flex-wrap items-center gap-1.5 text-sm text-zinc-700">
                  <MapPin size={15} weight="fill" className="text-amber-500" />
                  {[locSug.city, locSug.country].filter(Boolean).join(", ") || locSug.place_name}
                  <span className="flex items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700"><Sparkle size={10} weight="fill" /> AI-suggested{locSug.confidence ? ` · ${locSug.confidence}%` : ""}</span>
                </p>
                <p className="mt-1.5 text-xs italic text-zinc-500">{aa.why}</p>
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs font-medium text-zinc-700">
                  <Checkbox checked={verifyLoc} onCheckedChange={(v) => setVerifyLoc(!!v)} data-testid="review-verify-location" />
                  Verify this location (make it the confirmed location)
                </label>
              </div>
            ) : (
              <p className="mt-2 text-sm text-zinc-500" data-testid="review-loc-unknown">No GPS and AI couldn't determine a place — {aa.why || "please add a location manually if known."}</p>
            )}
          </div>

          {sug.nature?.length > 0 && (
            <div className="rounded-xl bg-emerald-50 p-3">
              <div className="mb-1 flex items-center gap-1.5"><Leaf size={14} className="text-emerald-600" weight="fill" /><span className="text-xs font-semibold">Species candidates</span></div>
              {sug.nature.map((n, i) => <p key={i} className="text-xs">{n.common_name} <span className="italic text-emerald-700">{n.scientific_name}</span> · {n.confidence}%</p>)}
            </div>
          )}
        </div>

        {/* Editable metadata (AI-suggested, editable) */}
        <div className="rounded-xl border border-border bg-white p-5">
          <div className="mb-3 flex items-center gap-1.5">
            <Sparkle size={15} weight="fill" className="text-[#F27A54]" />
            <p className="text-sm font-bold text-zinc-800">Suggested details</p>
            <span className="rounded-full bg-[#F27A54]/10 px-2 py-0.5 text-[10px] font-semibold text-[#B4451F]">AI · edit anything</span>
          </div>

          {/* Language tabs */}
          {langs.length > 1 && (
            <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="review-lang-tabs">
              <Translate size={14} weight="bold" className="text-[#2D6A4F]" />
              {langs.map((lg) => (
                <button key={lg} onClick={() => setLang(lg)} data-testid={`review-lang-${lg}`}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${lang === lg ? "bg-[#2D6A4F] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                  {LANG_LABEL[lg] || lg.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {loadingA ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Loading suggestions…</p>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block overline text-[10px] text-zinc-500">Title {lang !== "en" && `(${LANG_LABEL[lang] || lang})`}</label>
                <Input data-testid="review-title-input" value={titleBy[lang] || ""}
                  onChange={(e) => setTitleBy((s) => ({ ...s, [lang]: e.target.value }))}
                  placeholder={lang === "en" ? "Give this photo a title" : "Translation"} />
                {lang === "en" && sug.title && titleBy.en !== sug.title && (
                  <button data-testid="review-accept-title" onClick={() => setTitleBy((s) => ({ ...s, en: sug.title }))}
                    className="mt-1 text-[11px] font-semibold text-[#F27A54] hover:underline">Use AI: “{sug.title}”</button>
                )}
              </div>

              <div>
                <label className="mb-1 block overline text-[10px] text-zinc-500">Description {lang !== "en" && `(${LANG_LABEL[lang] || lang})`}</label>
                <Textarea data-testid="review-desc-input" rows={3} value={descBy[lang] || ""}
                  onChange={(e) => setDescBy((s) => ({ ...s, [lang]: e.target.value }))} placeholder="Describe the scene" />
              </div>

              <div>
                <label className="mb-1 block overline text-[10px] text-zinc-500">Alt text (accessibility) {lang !== "en" && `(${LANG_LABEL[lang] || lang})`}</label>
                <Input data-testid="review-alt-input" value={altBy[lang] || ""}
                  onChange={(e) => setAltBy((s) => ({ ...s, [lang]: e.target.value }))} placeholder="Describe the image for screen readers" />
              </div>

              {lang === "en" && (
                <>
                  <div>
                    <label className="mb-1 block overline text-[10px] text-zinc-500">Keywords (comma separated)</label>
                    <Input data-testid="review-keywords-input" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="sunset, coast, monsoon" />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(sug.categories || []).map((c, i) => <Badge key={i} variant="outline" className="rounded-sm text-xs">{c.category}{c.subcategory ? ` › ${c.subcategory}` : ""}</Badge>)}
                    {sug.primary_domain && <Badge className="rounded-sm bg-[#2D6A4F] text-xs text-white">{sug.primary_domain}</Badge>}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Rights & licensing */}
          {!loadingA && (
            <div className="mt-4 rounded-lg border border-border bg-[#FBFAF8] p-3" data-testid="review-rights">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-zinc-700"><ShieldCheck size={14} weight="fill" className="text-[#2D6A4F]" /> Rights &amp; licensing</p>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="review-creator" value={creator} onChange={(e) => setCreator(e.target.value)} placeholder="Creator / photographer" className="h-9 text-xs" />
                <Input data-testid="review-credit" value={creditLine} onChange={(e) => setCreditLine(e.target.value)} placeholder="Credit line" className="h-9 text-xs" />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
                {[["allow_embed", "Allow embeds"], ["allow_download", "Allow full download"], ["commercial_ok", "Commercial use OK"], ["editorial_only", "Editorial use only"], ["require_attribution", "Require attribution"]].map(([k, label]) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-xs text-zinc-700">
                    <Checkbox checked={!!rights[k]} onCheckedChange={(v) => setRights((r) => ({ ...r, [k]: !!v }))} data-testid={`review-right-${k}`} />
                    {label}
                  </label>
                ))}
                <label className="flex items-center gap-2 text-xs text-zinc-700">
                  <span>Attribution links to</span>
                  <select data-testid="review-attr-target" value={rights.attribution_target || "both"}
                    onChange={(e) => setRights((r) => ({ ...r, attribution_target: e.target.value }))}
                    className="rounded-md border border-border bg-white px-1.5 py-1 text-[11px]">
                    <option value="both">Both</option>
                    <option value="photo">Photo page</option>
                    <option value="photographer">Photographer</option>
                  </select>
                </label>
              </div>
              {!rights.allow_download && <p className="mt-1.5 text-[10px] text-zinc-500">Downloads off → visitors get a watermarked web-size preview; your original stays protected.</p>}
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-3">
            <Button data-testid="review-publish-btn" disabled={busy || loadingA} onClick={() => approve(true)}
              className="col-span-2 h-12 gap-2 rounded-md bg-[#F27A54] text-base font-bold hover:bg-[#d9633f]"><Lightning size={18} weight="fill" />Approve &amp; Publish</Button>
            <Button data-testid="review-approve-private-btn" disabled={busy || loadingA} onClick={() => approve(false)} variant="outline"
              className="h-11 gap-1.5 rounded-md text-sm"><CheckCircle size={16} weight="bold" />Approve (private)</Button>
            <Button data-testid="review-reject-btn" disabled={busy} onClick={reject} variant="outline"
              className="h-11 gap-1.5 rounded-md border-red-300 text-sm text-red-600 hover:bg-red-50"><XCircle size={16} weight="bold" />Reject</Button>
            <Button data-testid="review-skip-btn" disabled={busy} onClick={() => setIdx((idx + 1) % queue.length)} variant="ghost"
              className="col-span-2 h-9 gap-1.5 rounded-md text-xs text-muted-foreground"><SkipForward size={14} weight="bold" />Skip for now</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
